import { useAddProspectNoteMutation } from "@apis/prospects/useProspects"
import { useAuthUserDetails } from "@apis/users/useUsers"
import type { IAddProspectNote } from "@apis/prospects/types"
import { AxiosError } from "axios"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Button } from "@components/ui/button"
import { Textarea } from "@components/ui/textarea"
import { Loader2 } from "lucide-react"

type TAddNoteFormProps = {
  prospect_guid: string
  onCancel: () => void
  onSubmitSuccess?: () => void
}

export const AddProspectNoteForm = ({
  prospect_guid,
  onCancel,
  onSubmitSuccess,
}: TAddNoteFormProps) => {
  const form = useForm<IAddProspectNote>({
    mode: "onTouched",
  })

  const authUserDetails = useAuthUserDetails()
  const { mutate, isPending } = useAddProspectNoteMutation()

  const handleAddNote = (formData: IAddProspectNote) => {
    const data = {
      note: formData.note.trim(),
      agent_id: authUserDetails.data?.email || "",
      agent_name: authUserDetails.data?.email || "",
    }
    mutate(
      {
        prospect_guid,
        data,
      },
      {
        onSuccess: () => {
          toast.success("Note successfully Added!")
          onSubmitSuccess?.()
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error adding note! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="tw:space-y-6">
        <div className="tw:grid tw:grid-cols-1 tw:gap-4">
          <FormField
            control={form.control}
            name="note"
            rules={{ required: "Note is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Note</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Add a note about this prospect..."
                    rows={5}
                    autoFocus
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="tw:flex tw:justify-end tw:gap-4">
          <Button
            type="submit"
            disabled={!form.formState.isValid || isPending}
            onClick={form.handleSubmit(handleAddNote)}
          >
            {isPending ? (
              <>
                <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onCancel?.()}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  )
}
